/**
 * Foreign glTF payload — everything in a scene document that Trace did not
 * author — carried verbatim from load to the next save (issue #834).
 *
 * CONTENT_MODEL_SPEC §2.4 has the server store a client document byte for
 * byte, and §3.6 makes the document the one home for what a scene says about
 * itself. The tools are custodians of that document, not its owners: a
 * hand-authored `extras.vendor`, a `KHR_xmp_json_ld` block, an
 * `extensionsRequired` entry must survive a transform edit, because the writer
 * re-serialises the whole document on every save and anything it does not
 * carry is deleted.
 *
 * So the writer owns exactly three things — `extras.trace`, the extensions it
 * generates (`KHR_interactivity`), and the node extensions that hold indices it
 * renumbers (`TRACE_animation_binding`) — and everything else is passed through.
 * These helpers are the split: they lift the foreign half off a normalized
 * document at load, and the writer merges it back with the Trace half last, so
 * a diff of two saves reads naturally.
 *
 * **Caveat (spec §3.6).** A foreign extension that stores node/mesh/file
 * INDICES is preserved verbatim and is not remapped: a topology edit can leave
 * its indices pointing at something else. Nothing here can make that safe —
 * only the extension's own author knows what its numbers mean — and dropping
 * the block instead would lose the data outright, which is the bug this
 * module exists to fix.
 */

import type { NormalizedNode, NormalizedSceneDocument } from './types';

/** The key the Trace payload lives under, in every `extras` object. */
const TRACE_KEY = 'trace';

/** The one extension the writer generates, and therefore owns. */
const GENERATED_EXTENSIONS: readonly string[] = ['KHR_interactivity'];

/**
 * Node extensions the reader and writer OWN: they hold indices the writer must
 * remap when it renumbers `files[]`/`externalAssets[]`, so they can never be
 * passed through verbatim (TRACE_animation_binding §4).
 */
export const OWNED_NODE_EXTENSIONS: readonly string[] = ['TRACE_animation_binding'];

/**
 * A node's foreign payload: its `extras` minus the Trace key, and its
 * `extensions` verbatim.
 *
 * Absent entirely when the node carries nothing foreign — an empty bag would
 * make the writer emit `"extras": {}` where the document had no extras at all.
 */
export interface ForeignNodeData {
    /** `node.extras` minus `trace`. */
    extras?: Record<string, unknown>;
    /** `node.extensions`, verbatim. */
    extensions?: Record<string, unknown>;
}

/**
 * The document's foreign payload — the half of a scene document the writer
 * does not author.
 */
export interface ForeignDocumentData {
    /** Document `extras` minus `trace`. */
    extras?: Record<string, unknown>;
    /** The default scene's `extras` minus `trace`. */
    sceneExtras?: Record<string, unknown>;
    /** `asset.extras`, verbatim. */
    assetExtras?: Record<string, unknown>;
    /** Document `extensions` minus the ones the writer generates. */
    extensions?: Record<string, unknown>;
    /** `extensionsUsed`, verbatim — the writer unions its own onto it. */
    extensionsUsed?: string[];
    /** `extensionsRequired`, verbatim. */
    extensionsRequired?: string[];
}

/** A plain object, or undefined for anything else (arrays included). */
function asObject(value: unknown): Record<string, unknown> | undefined {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : undefined;
}

/**
 * The keys of `source` that are not in `excluded`, or undefined when none are.
 *
 * "None are" and "no object at all" answer the same, deliberately: both mean
 * there is nothing to write back, and the writer must not invent an empty
 * object for either.
 */
function without(
    source: unknown,
    excluded: readonly string[],
): Record<string, unknown> | undefined {
    const object = asObject(source);
    if (!object) return undefined;
    const kept: Record<string, unknown> = {};
    let any = false;
    for (const key of Object.keys(object)) {
        if (excluded.includes(key)) continue;
        kept[key] = object[key];
        any = true;
    }
    return any ? kept : undefined;
}

/** Drop the keys whose values are undefined, and the bag itself when empty. */
function bag<T extends object>(value: T): T | undefined {
    return Object.values(value).some(v => v !== undefined) ? value : undefined;
}

/**
 * The foreign payload of one normalized node, or undefined when it has none.
 */
export function foreignNodeData(node: NormalizedNode): ForeignNodeData | undefined {
    const extras = without(node.extras, [TRACE_KEY]);
    const extensions = without(node.extensions, OWNED_NODE_EXTENSIONS);
    return bag({
        ...(extras ? { extras } : {}),
        ...(extensions ? { extensions } : {}),
    });
}

/**
 * The foreign payload of a normalized document, or undefined when it has none.
 *
 * The default scene is the one whose extras are carried: it is the only scene
 * the writer emits (`scenes[0]`), so it is the only one a round trip can put
 * back.
 */
export function foreignDocumentData(
    document: NormalizedSceneDocument,
): ForeignDocumentData | undefined {
    const scene = document.scenes[document.scene] ?? document.scenes[0];
    const extras = without(document.extras, [TRACE_KEY]);
    const sceneExtras = without(scene?.extras, [TRACE_KEY]);
    const assetExtras = without(document.assetExtras, []);
    const extensions = without(document.extensions, GENERATED_EXTENSIONS);
    const extensionsUsed = document.extensionsUsed?.length ? [...document.extensionsUsed] : undefined;
    const extensionsRequired = document.extensionsRequired?.length
        ? [...document.extensionsRequired]
        : undefined;
    return bag({
        ...(extras ? { extras } : {}),
        ...(sceneExtras ? { sceneExtras } : {}),
        ...(assetExtras ? { assetExtras } : {}),
        ...(extensions ? { extensions } : {}),
        ...(extensionsUsed ? { extensionsUsed } : {}),
        ...(extensionsRequired ? { extensionsRequired } : {}),
    });
}

/**
 * Merge a node's / document's foreign `extras` keys with the `extras` object
 * the writer just built (which holds `trace`, and nothing else).
 *
 * Foreign keys first, `trace` last — a save is then a readable diff of the
 * previous one rather than a reshuffle. Returns undefined when there is
 * nothing at all to write, so a document with neither keeps emitting no
 * `extras` key, byte for byte as before.
 */
export function mergeForeignExtras(
    foreign: Record<string, unknown> | undefined,
    written: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
    if (!foreign) return written;
    return { ...foreign, ...(written ?? {}) };
}
